import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { apiErr, formatNum } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input, Label } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, GitBranch, Power, PowerOff, Check } from "lucide-react";
import { toast } from "sonner";
import { PageShell, Stat } from "./PageShell";

const BLANK = {
  code: "", registry: "SEFR", name: "", scope: 1, category: "", unit: "",
  factor_kgco2e: "", region: "", issuing_body: "", source: "", ipcc_version: "",
  sub_category: "", version: "", effective_date: "", boundary_inclusion: "", description: "",
};

function FactorForm({ form, setForm, editing }) {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
      <div><Label className="text-xs">Code *</Label><Input value={form.code} disabled={editing} onChange={(e) => set("code", e.target.value)} placeholder="SEFR-FUEL-099" data-testid="ff-code" /></div>
      <div><Label className="text-xs">Registry *</Label>
        <Select value={form.registry} onValueChange={(v) => set("registry", v)}>
          <SelectTrigger data-testid="ff-registry"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="SEFR">SEFR · Singapore</SelectItem><SelectItem value="IPCC">IPCC · international</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="col-span-2"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Combustion of Diesel" data-testid="ff-name" /></div>
      <div><Label className="text-xs">Scope *</Label>
        <Select value={String(form.scope)} onValueChange={(v) => set("scope", Number(v))}>
          <SelectTrigger data-testid="ff-scope"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="1">Scope 1</SelectItem><SelectItem value="2">Scope 2</SelectItem><SelectItem value="3">Scope 3</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Category *</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Fuel" data-testid="ff-category" /></div>
      <div><Label className="text-xs">Sub-category</Label><Input value={form.sub_category} onChange={(e) => set("sub_category", e.target.value)} /></div>
      <div><Label className="text-xs">Unit *</Label><Input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="metric ton, kWh, kg…" data-testid="ff-unit" /></div>
      <div><Label className="text-xs">Factor (kgCO₂e/unit)</Label><Input type="number" step="any" value={form.factor_kgco2e} onChange={(e) => set("factor_kgco2e", e.target.value)} placeholder="blank = value pending" data-testid="ff-value" /></div>
      <div><Label className="text-xs">Region</Label><Input value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="SG / INTL" /></div>
      <div><Label className="text-xs">Issuing body</Label><Input value={form.issuing_body} onChange={(e) => set("issuing_body", e.target.value)} placeholder="NEA / EMA / IPCC" /></div>
      <div><Label className="text-xs">IPCC version</Label><Input value={form.ipcc_version} onChange={(e) => set("ipcc_version", e.target.value)} placeholder="AR5 / AR6" /></div>
      <div><Label className="text-xs">Version</Label><Input value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="v1" /></div>
      <div><Label className="text-xs">Effective date</Label><Input value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} placeholder="2026-01-01" /></div>
      <div className="col-span-2"><Label className="text-xs">Source</Label><Input value={form.source} onChange={(e) => set("source", e.target.value)} /></div>
      <div className="col-span-2"><Label className="text-xs">Boundary / note</Label><Input value={form.boundary_inclusion} onChange={(e) => set("boundary_inclusion", e.target.value)} /></div>
    </div>
  );
}

export default function PlatformEmissionFactors() {
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState(null);
  const [registry, setRegistry] = useState("all");
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [supersede, setSupersede] = useState(null);
  const [sForm, setSForm] = useState({ new_code: "", factor_kgco2e: "", effective_date: "" });
  const [addForm, setAddForm] = useState(BLANK);
  const [pendingVal, setPendingVal] = useState({});

  const load = useCallback(() => {
    const params = {};
    if (registry !== "all") params.registry = registry;
    if (status !== "all") params.status = status;
    if (active !== "all") params.active = active;
    if (q) params.q = q;
    api.get("/factor-admin/factors", { params }).then((r) => setRows(r.data.factors)).catch((e) => toast.error(apiErr(e)));
    api.get("/emissions/factors/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, [registry, status, active, q]);
  useEffect(() => { load(); }, [registry, status, active]); // eslint-disable-line

  const createFactor = async () => {
    try {
      const body = { ...addForm, factor_kgco2e: addForm.factor_kgco2e === "" ? null : Number(addForm.factor_kgco2e) };
      await api.post("/factor-admin/factors", body);
      toast.success("Factor created"); setAddOpen(false); setAddForm(BLANK); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    try {
      const { code, ...rest } = editForm;
      const body = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== "" && v !== null));
      if (body.factor_kgco2e !== undefined) body.factor_kgco2e = Number(body.factor_kgco2e);
      if (body.scope !== undefined) body.scope = Number(body.scope);
      await api.patch(`/factor-admin/factors/${encodeURIComponent(code)}`, body);
      toast.success("Factor updated"); setEditForm(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doSupersede = async () => {
    try {
      await api.post(`/factor-admin/factors/${encodeURIComponent(supersede.code)}/supersede`, {
        new_code: sForm.new_code, effective_date: sForm.effective_date || undefined,
        factor_kgco2e: sForm.factor_kgco2e === "" ? undefined : Number(sForm.factor_kgco2e),
      });
      toast.success("New version created — old marked inactive"); setSupersede(null); setSForm({ new_code: "", factor_kgco2e: "", effective_date: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleActive = async (f) => {
    try {
      await api.post(`/factor-admin/factors/${encodeURIComponent(f.code)}/${f.active === false ? "reactivate" : "deactivate"}`);
      toast.success(f.active === false ? "Reactivated" : "Deactivated"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const fillValue = async (code) => {
    const v = pendingVal[code];
    if (v === undefined || v === "") return;
    try {
      await api.post(`/factor-admin/factors/${encodeURIComponent(code)}/set-value`, { factor_kgco2e: Number(v) });
      toast.success("Value filled"); setPendingVal((p) => ({ ...p, [code]: "" })); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sefr = facets?.registries?.find((r) => r.registry === "SEFR")?.count || 0;
  const ipcc = facets?.registries?.find((r) => r.registry === "IPCC")?.count || 0;

  return (
    <PageShell eyebrow="Platform Operations" title="Emission Factor Console"
      subtitle="Add, edit, version and curate SEFR & IPCC factors, and clear the value-pending queue. New versions supersede the old (kept inactive for history)."
      testid="platform-emission-factors"
      actions={<Button variant="primary" onClick={() => { setAddForm(BLANK); setAddOpen(true); }} data-testid="add-factor-btn"><Plus className="h-3.5 w-3.5" /> Add factor</Button>}>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="SEFR · Singapore" value={sefr} tone="accent" />
        <Stat label="IPCC · international" value={ipcc} />
        <Stat label="Value pending" value={facets?.pending_count ?? "—"} tone="amber" />
        <Stat label="Total factors" value={facets?.total ?? "—"} />
      </div>

      <div className="card-flat p-3 flex flex-wrap items-center gap-3">
        <Input placeholder="Search code / name / category…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-xs" data-testid="fa-search" />
        <Select value={registry} onValueChange={setRegistry}>
          <SelectTrigger className="w-40" data-testid="fa-registry"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All registries</SelectItem><SelectItem value="SEFR">SEFR</SelectItem><SelectItem value="IPCC">IPCC</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="fa-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All values</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="pending">Value pending</SelectItem></SelectContent>
        </Select>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="w-36" data-testid="fa-active"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Active + inactive</SelectItem><SelectItem value="true">Active only</SelectItem><SelectItem value="false">Inactive only</SelectItem></SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="fa-search-btn">Search</Button>
        <span className="ml-auto text-xs text-muted-foreground" data-testid="fa-count">{rows.length} shown</span>
      </div>

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Registry</TableHead><TableHead>Code</TableHead><TableHead>Name</TableHead>
              <TableHead>Category</TableHead><TableHead>Scope</TableHead>
              <TableHead className="text-right">kgCO₂e/unit</TableHead><TableHead>Version</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">No factors match.</TableCell></TableRow>}
            {rows.map((f) => (
              <TableRow key={f.code} data-testid="fa-row" className={f.active === false ? "opacity-55" : ""}>
                <TableCell><Badge variant={f.registry === "SEFR" ? "lime" : "outline"} className="text-[9px]">{f.registry === "SEFR" ? "SEFR·SG" : "IPCC"}</Badge></TableCell>
                <TableCell className="font-mono text-[11px]">{f.code}</TableCell>
                <TableCell className="text-xs">{f.name}<div className="text-[10px] text-muted-foreground">{f.issuing_body || f.source || ""}{f.ipcc_version ? ` · ${f.ipcc_version}` : ""}</div></TableCell>
                <TableCell className="text-xs text-muted-foreground">{f.category}</TableCell>
                <TableCell><Badge variant="outline" className="text-[9px]">S{f.scope}</Badge></TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {f.value_status === "pending" || f.factor_kgco2e == null ? (
                    <div className="flex items-center gap-1 justify-end">
                      <Input type="number" step="any" value={pendingVal[f.code] ?? ""} onChange={(e) => setPendingVal((p) => ({ ...p, [f.code]: e.target.value }))} className="h-7 w-24 text-xs" placeholder="pending" data-testid={`fa-fill-input-${f.code}`} />
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => fillValue(f.code)} data-testid={`fa-fill-btn-${f.code}`}><Check className="h-3.5 w-3.5 text-accent" /></Button>
                    </div>
                  ) : <>{formatNum(f.factor_kgco2e, { decimals: 4 })} <span className="text-muted-foreground text-[10px]">/{f.unit}</span></>}
                </TableCell>
                <TableCell className="text-[11px] text-muted-foreground">{f.version || "—"}{f.effective_date ? ` · ${f.effective_date}` : ""}</TableCell>
                <TableCell>
                  {f.active === false
                    ? <Badge variant="outline" className="text-[9px]" title={f.superseded_by ? `Superseded by ${f.superseded_by}` : "Inactive"}>Inactive</Badge>
                    : <Badge variant="success" className="text-[9px]">Active</Badge>}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5 justify-end">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => setEditForm({ code: f.code, registry: f.registry, name: f.name, scope: f.scope, category: f.category, unit: f.unit, factor_kgco2e: f.factor_kgco2e ?? "", issuing_body: f.issuing_body || "", source: f.source || "", ipcc_version: f.ipcc_version || "", version: f.version || "", effective_date: f.effective_date || "", boundary_inclusion: f.boundary_inclusion || "", description: f.description || "", sub_category: f.sub_category || "", region: f.region || "" })} data-testid={`fa-edit-${f.code}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="New version" onClick={() => { setSupersede(f); setSForm({ new_code: `${f.code}-v2`, factor_kgco2e: f.factor_kgco2e ?? "", effective_date: "" }); }} data-testid={`fa-supersede-${f.code}`}><GitBranch className="h-3.5 w-3.5 text-accent" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={f.active === false ? "Reactivate" : "Deactivate"} onClick={() => toggleActive(f)} data-testid={`fa-toggle-${f.code}`}>{f.active === false ? <Power className="h-3.5 w-3.5 text-[#00E65B]" /> : <PowerOff className="h-3.5 w-3.5 text-red-400" />}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-factor-dialog">
          <DialogHeader><DialogTitle className="font-display">Add emission factor</DialogTitle></DialogHeader>
          <FactorForm form={addForm} setForm={setAddForm} editing={false} />
          <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button variant="primary" onClick={createFactor} data-testid="fa-create-btn">Create</Button></div>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editForm} onOpenChange={(o) => !o && setEditForm(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-factor-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit {editForm?.code}</DialogTitle></DialogHeader>
          {editForm && <FactorForm form={editForm} setForm={setEditForm} editing />}
          <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={() => setEditForm(null)}>Cancel</Button><Button variant="primary" onClick={saveEdit} data-testid="fa-save-edit-btn">Save</Button></div>
        </DialogContent>
      </Dialog>

      {/* Supersede */}
      <Dialog open={!!supersede} onOpenChange={(o) => !o && setSupersede(null)}>
        <DialogContent className="max-w-md" data-testid="supersede-dialog">
          <DialogHeader><DialogTitle className="font-display">New version of {supersede?.code}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Creates a new factor and marks <span className="font-mono">{supersede?.code}</span> inactive (kept for history). Existing records keep their applied factor.</p>
          <div><Label className="text-xs">New code *</Label><Input value={sForm.new_code} onChange={(e) => setSForm((p) => ({ ...p, new_code: e.target.value }))} data-testid="ss-code" /></div>
          <div><Label className="text-xs">New value (kgCO₂e/unit)</Label><Input type="number" step="any" value={sForm.factor_kgco2e} onChange={(e) => setSForm((p) => ({ ...p, factor_kgco2e: e.target.value }))} data-testid="ss-value" /></div>
          <div><Label className="text-xs">Effective date</Label><Input value={sForm.effective_date} onChange={(e) => setSForm((p) => ({ ...p, effective_date: e.target.value }))} placeholder="2026-01-01" data-testid="ss-date" /></div>
          <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={() => setSupersede(null)}>Cancel</Button><Button variant="primary" disabled={!sForm.new_code} onClick={doSupersede} data-testid="ss-apply-btn">Create version</Button></div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
